import React, { useEffect, useMemo, useRef, useState } from 'react';
import AdminTenantSelector, { type AdminTenantOption } from '../components/AdminTenantSelector';
import { usePortalContext } from '../context/PortalContext';
import InstanceCard from '../components/installations/InstanceCard';
import InstancesAttentionSummary from '../components/installations/InstancesAttentionSummary';
import ZeroTouchCard from '../components/installations/ZeroTouchCard';

interface InstanceView {
  instanceId: string;
  siteUrl: string;
  pageUrl: string;
  webPartVersion: string;
  schemaVersion: string | null;
  mode: string;
  registeredAt: string;
  lastSeenAt: string;
  deregisteredAt: string | null;
  status: 'active' | 'recent' | 'stale' | 'superseded' | 'deregistered';
  isOutdated: boolean;
  daysSinceLastSeen: number;
  supersededBy?: { instanceId: string; registeredAt: string } | null;
  listVisibility?: {
    visibilityState: 'hidden' | 'revealed';
    protectedListsPolicy: 'keep_hidden';
    revealStartedAt: string | null;
    expiresAt: string | null;
    lastAppliedAt: string | null;
    lastError: string | null;
  };
  attention?: {
    runtimeGate: string;
    installerState: string | null;
    reason: string | null;
    notificationDispatched: boolean;
    notificationSummary: string | null;
    updatedAt: string;
  } | null;
  maintenance?: {
    operationId: string;
    requestedAction: 'install' | 'repair' | 'upgrade';
    status: 'pending' | 'running' | 'succeeded' | 'failed' | 'cancelled';
    stage: string;
    statusMessage: string | null;
    updatedAt: string;
  } | null;
  mailDelivery?: {
    uncertainCount: number;
    heldCount: number;
    lastUncertainAt: string | null;
  };
}

interface VerifySiteResult {
  siteUrl: string;
  outcome: 'reachable' | 'not_found' | 'no_access' | 'error';
  httpStatus: number | null;
  detail: string | null;
  instanceIds: string[];
  deregisteredInstanceIds: string[];
}

interface VerifyResult {
  checkedSites: number;
  reachable: number;
  notFound: number;
  noAccess: number;
  errors: number;
  deregisteredCount: number;
  sites: VerifySiteResult[];
}

interface InstanceSummary {
  totalActive: number;
  totalStale: number;
  totalSuperseded?: number;
  totalDeregistered: number;
  oldestVersion: string | null;
  newestVersion: string | null;
  needsUpdate: number;
  needsAttention: number;
  maintenanceRunning: number;
}

interface InstanceAdminMember {
  title: string;
  loginName: string;
  email: string;
}

interface InstanceAdminInspection {
  fetchedAt: string;
  cached: boolean;
  memberCount: number;
  members: InstanceAdminMember[];
}

type InspectorConnectionStatus = 'pending' | 'connected' | 'error' | 'disconnected';
type InspectorGrantStatus = 'pending' | 'granted' | 'revoked' | 'error';

interface InspectorConnection {
  connectionStatus: InspectorConnectionStatus;
  directoryTenantId: string;
  consentGrantedAt: string | null;
  lastError: string | null;
  updatedAt: string;
}

interface InspectorSiteGrant {
  siteUrl: string;
  siteUrlNormalized: string;
  grantStatus: InspectorGrantStatus;
  grantedAt: string | null;
  lastError: string | null;
  updatedAt: string;
}

interface InspectorStatusResponse {
  configured: boolean;
  tenantId?: string;
  interactiveSetupAvailable?: boolean;
  siteAssignment?: { requiresCustomerAdmin: boolean; appClientId: string; role: 'read' };
  connection: InspectorConnection | null;
  siteGrants: InspectorSiteGrant[];
}

type HeartbeatHealth = 'fresh' | 'low' | 'stale';

const relativeTime = (dateStr: string): string => {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
  const days = Math.floor(seconds / 86400);
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return `${Math.floor(days / 30)} months ago`;
};

const heartbeatAgeMinutes = (dateStr: string): number => {
  const ms = Date.now() - new Date(dateStr).getTime();
  if (!Number.isFinite(ms) || ms < 0) return 0;
  return Math.floor(ms / 60000);
};

const getHeartbeatHealth = (dateStr: string): HeartbeatHealth => {
  const minutes = heartbeatAgeMinutes(dateStr);
  if (minutes <= 10) return 'fresh';
  if (minutes <= 60) return 'low';
  return 'stale';
};

const prettyHost = (url: string): string => {
  try {
    return new URL(url).host + new URL(url).pathname;
  } catch {
    return url;
  }
};

const normalizeSiteKey = (url: string): string => {
  try {
    const parsed = new URL(url);
    const normalizedPath = parsed.pathname.replace(/\/+$/, '') || '/';
    return `${parsed.protocol}//${parsed.hostname.toLowerCase()}${normalizedPath}${parsed.search}`.toLowerCase();
  } catch {
    return String(url ?? '').trim().toLowerCase();
  }
};

const Instances = (): JSX.Element => {
  const { tenant } = usePortalContext();
  const canManageInstallations = tenant?.capabilities?.canManageInstallations === true;
  const [deployableTenants, setDeployableTenants] = useState<AdminTenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [instances, setInstances] = useState<InstanceView[]>([]);
  const [summary, setSummary] = useState<InstanceSummary | null>(null);
  const [currentVersion, setCurrentVersion] = useState<string>('');
  const [currentVersionSource, setCurrentVersionSource] = useState<'release-index' | 'config' | ''>('');
  const [dismissLoading, setDismissLoading] = useState<Record<string, boolean>>({});
  const [dismissError, setDismissError] = useState<Record<string, string>>({});
  const [deregisterLoading, setDeregisterLoading] = useState<Record<string, boolean>>({});
  const [deregisterError, setDeregisterError] = useState<Record<string, string>>({});
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verifyResult, setVerifyResult] = useState<VerifyResult | null>(null);
  const [requiredSchemaVersion, setRequiredSchemaVersion] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [degraded, setDegraded] = useState<boolean>(false);
  const [showDeregistered, setShowDeregistered] = useState(false);
  const [adminInspections, setAdminInspections] = useState<Record<string, InstanceAdminInspection>>({});
  const [inspectLoading, setInspectLoading] = useState<Record<string, boolean>>({});
  const [inspectError, setInspectError] = useState<Record<string, string>>({});
  const [inspectorConfigured, setInspectorConfigured] = useState(false);
  const [interactiveSetupAvailable, setInteractiveSetupAvailable] = useState(false);
  const [inspectorAppClientId, setInspectorAppClientId] = useState('');
  const [inspectorConnection, setInspectorConnection] = useState<InspectorConnection | null>(null);
  const [inspectorSiteGrants, setInspectorSiteGrants] = useState<InspectorSiteGrant[]>([]);
  const [inspectorLoading, setInspectorLoading] = useState(false);
  const [inspectorError, setInspectorError] = useState<string | null>(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const [grantLoading, setGrantLoading] = useState<Record<string, boolean>>({});
  const [grantError, setGrantError] = useState<Record<string, string>>({});
  const [listVisibilityLoading, setListVisibilityLoading] = useState<Record<string, boolean>>({});
  const [listVisibilityError, setListVisibilityError] = useState<Record<string, string>>({});
  const [maintenanceLoading, setMaintenanceLoading] = useState<Record<string, boolean>>({});
  const maintenanceInFlightRef = useRef<Set<string>>(new Set());
  const [maintenanceError, setMaintenanceError] = useState<Record<string, string>>({});
  const [mailResendLoading, setMailResendLoading] = useState<Record<string, boolean>>({});
  const [mailResendError, setMailResendError] = useState<Record<string, string>>({});

  const selectedTenant = useMemo(
    () => deployableTenants.find((entry) => entry.tenantId === selectedTenantId) ?? null,
    [deployableTenants, selectedTenantId]
  );

  const loadTenants = async (): Promise<void> => {
    if (!canManageInstallations) {
      setLoading(false);
      return;
    }
    try {
      const response = await fetch('/api/admin/spfx-releases/deployable-tenants');
      if (!response.ok) throw new Error(`Failed to load tenants (${response.status})`);
      const payload = (await response.json()) as {
        tenants?: AdminTenantOption[];
        defaultTenantId?: string;
      };
      const tenants = Array.isArray(payload.tenants) ? payload.tenants : [];
      setDeployableTenants(tenants);
      setSelectedTenantId((current) => current || payload.defaultTenantId || tenants[0]?.tenantId || '');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const loadInstances = async (): Promise<void> => {
    if (!selectedTenantId) {
      return;
    }
    setLoading(true);
    setError(null);
    setDegraded(false);
    try {
      const params = new URLSearchParams({ tenantId: selectedTenantId });
      const response = await fetch(`/api/instances?${params.toString()}`);
      if (!response.ok) throw new Error(`Failed to load instances (${response.status})`);
      const data = (await response.json()) as {
        instances?: InstanceView[];
        summary?: InstanceSummary;
        currentVersion?: string;
        currentVersionSource?: 'release-index' | 'config';
        requiredSchemaVersion?: string;
        degraded?: boolean;
      };
      setInstances(Array.isArray(data.instances) ? data.instances : []);
      setSummary(data.summary ?? null);
      setCurrentVersion(data.currentVersion ?? '');
      setCurrentVersionSource(data.currentVersionSource ?? '');
      setRequiredSchemaVersion(data.requiredSchemaVersion ?? '');
      setDegraded(data.degraded === true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const requestMaintenance = async (
    instanceId: string,
    action: 'repair'
  ): Promise<void> => {
    // Synchronous guard: React state updates batch, so two clicks before the
    // next render would both reach the server and the second would be
    // cancelled by the race guard, hiding the live repair on the card.
    if (maintenanceInFlightRef.current.has(instanceId)) {
      return;
    }
    maintenanceInFlightRef.current.add(instanceId);
    setMaintenanceLoading((prev) => ({ ...prev, [instanceId]: true }));
    setMaintenanceError((prev) => ({ ...prev, [instanceId]: '' }));
    try {
      const params = new URLSearchParams({ tenantId: selectedTenantId });
      const response = await fetch(`/api/instances/${encodeURIComponent(instanceId)}/maintenance?${params.toString()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action })
      });
      const payload = (await response.json().catch(() => ({}))) as { message?: string };
      if (!response.ok) {
        throw new Error(payload.message ?? `Maintenance request failed (${response.status})`);
      }
      await loadInstances();
    } catch (error) {
      setMaintenanceError((prev) => ({ ...prev, [instanceId]: (error as Error).message }));
      // Reload anyway: a refused request usually means the server state
      // differs from what the card showed (a live operation, an expiry).
      await loadInstances().catch(() => undefined);
    } finally {
      maintenanceInFlightRef.current.delete(instanceId);
      setMaintenanceLoading((prev) => ({ ...prev, [instanceId]: false }));
    }
  };

  const dismissMaintenance = async (instanceId: string, operationId: string): Promise<void> => {
    setDismissLoading((prev) => ({ ...prev, [instanceId]: true }));
    setDismissError((prev) => ({ ...prev, [instanceId]: '' }));
    try {
      const params = new URLSearchParams({ tenantId: selectedTenantId });
      const response = await fetch(
        `/api/instances/${encodeURIComponent(instanceId)}/maintenance/${encodeURIComponent(operationId)}/dismiss?${params.toString()}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tenantId: selectedTenantId }) }
      );
      const payload = (await response.json().catch(() => ({}))) as { message?: string };
      if (!response.ok) {
        throw new Error(payload.message ?? `Dismiss failed (${response.status})`);
      }
      await loadInstances();
    } catch (error) {
      setDismissError((prev) => ({ ...prev, [instanceId]: (error as Error).message }));
    } finally {
      setDismissLoading((prev) => ({ ...prev, [instanceId]: false }));
    }
  };

  const cancelMaintenance = async (instanceId: string, operationId: string): Promise<void> => {
    setDismissLoading((prev) => ({ ...prev, [instanceId]: true }));
    setDismissError((prev) => ({ ...prev, [instanceId]: '' }));
    try {
      const params = new URLSearchParams({ tenantId: selectedTenantId });
      const response = await fetch(
        `/api/instances/${encodeURIComponent(instanceId)}/maintenance/${encodeURIComponent(operationId)}/cancel?${params.toString()}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tenantId: selectedTenantId }) }
      );
      const payload = (await response.json().catch(() => ({}))) as { message?: string };
      if (!response.ok) {
        throw new Error(payload.message ?? `Cancel failed (${response.status})`);
      }
      await loadInstances();
    } catch (error) {
      setDismissError((prev) => ({ ...prev, [instanceId]: (error as Error).message }));
    } finally {
      setDismissLoading((prev) => ({ ...prev, [instanceId]: false }));
    }
  };

  const verifyInstallations = async (): Promise<void> => {
    setVerifyLoading(true);
    setVerifyError(null);
    try {
      const params = new URLSearchParams({ tenantId: selectedTenantId });
      const response = await fetch(`/api/instances/verify?${params.toString()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: selectedTenantId })
      });
      const payload = (await response.json().catch(() => ({}))) as VerifyResult & { message?: string };
      if (!response.ok) {
        throw new Error(payload.message ?? `Verification failed (${response.status})`);
      }
      setVerifyResult(payload);
      await loadInstances();
    } catch (err) {
      setVerifyError((err as Error).message);
    } finally {
      setVerifyLoading(false);
    }
  };

  const deregisterInstance = async (instanceId: string): Promise<void> => {
    setDeregisterLoading((prev) => ({ ...prev, [instanceId]: true }));
    setDeregisterError((prev) => ({ ...prev, [instanceId]: '' }));
    try {
      const params = new URLSearchParams({ tenantId: selectedTenantId });
      const response = await fetch(`/api/instances/${encodeURIComponent(instanceId)}/deregister?${params.toString()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: selectedTenantId })
      });
      const payload = (await response.json().catch(() => ({}))) as { message?: string };
      if (!response.ok) {
        throw new Error(payload.message ?? `Deregister failed (${response.status})`);
      }
      await loadInstances();
    } catch (err) {
      setDeregisterError((prev) => ({ ...prev, [instanceId]: (err as Error).message }));
    } finally {
      setDeregisterLoading((prev) => ({ ...prev, [instanceId]: false }));
    }
  };

  const resendHeldMail = async (instanceId: string): Promise<void> => {
    setMailResendLoading(prev => ({ ...prev, [instanceId]: true }));
    setMailResendError(prev => ({ ...prev, [instanceId]: '' }));
    try {
      const params = new URLSearchParams({ tenantId: selectedTenantId });
      const response = await fetch(`/api/instances/${encodeURIComponent(instanceId)}/mail-delivery/resend-held?${params.toString()}`, {
        method: 'POST'
      });
      const payload = (await response.json().catch(() => ({}))) as { message?: string };
      if (!response.ok) throw new Error(payload.message ?? `Resend request failed (${response.status})`);
      await loadInstances();
    } catch (error) {
      setMailResendError(prev => ({ ...prev, [instanceId]: (error as Error).message }));
    } finally {
      setMailResendLoading(prev => ({ ...prev, [instanceId]: false }));
    }
  };

  const loadInspectorStatus = async (): Promise<void> => {
    if (!selectedTenantId) {
      return;
    }
    setInspectorLoading(true);
    setInspectorError(null);
    try {
      const params = new URLSearchParams({ tenantId: selectedTenantId });
      const response = await fetch(`/api/inspector/status?${params.toString()}`);
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(payload.message ?? `Inspector status failed (${response.status})`);
      }
      const payload = (await response.json()) as InspectorStatusResponse;
      setInspectorConfigured(payload.configured === true);
      setInteractiveSetupAvailable(payload.interactiveSetupAvailable === true);
      setInspectorAppClientId(payload.siteAssignment?.appClientId ?? '');
      setInspectorConnection(payload.connection ?? null);
      setInspectorSiteGrants(Array.isArray(payload.siteGrants) ? payload.siteGrants : []);
    } catch (error) {
      setInspectorError((error as Error).message);
      setInspectorConfigured(false);
      setInteractiveSetupAvailable(false);
      setInspectorAppClientId('');
      setInspectorConnection(null);
      setInspectorSiteGrants([]);
    } finally {
      setInspectorLoading(false);
    }
  };

  useEffect(() => {
    void loadTenants();
  }, [canManageInstallations]);

  useEffect(() => {
    if (!canManageInstallations) {
      return;
    }
    void loadInstances();
    void loadInspectorStatus();
  }, [canManageInstallations, selectedTenantId]);

  const startInspectorConnect = async (): Promise<void> => {
    setConnectLoading(true);
    setInspectorError(null);
    try {
      const response = await fetch('/api/inspector/connect/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: selectedTenantId })
      });
      const payload = (await response.json().catch(() => ({}))) as { authorizationUrl?: string; message?: string };
      if (!response.ok || !payload.authorizationUrl) {
        throw new Error(payload.message ?? `Connect start failed (${response.status})`);
      }
      window.location.href = payload.authorizationUrl;
    } catch (error) {
      setInspectorError((error as Error).message);
      setConnectLoading(false);
    }
  };

  const disconnectInspector = async (): Promise<void> => {
    setDisconnectLoading(true);
    setInspectorError(null);
    try {
      const response = await fetch('/api/inspector/disconnect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: selectedTenantId })
      });
      const payload = (await response.json().catch(() => ({}))) as { message?: string };
      if (!response.ok) {
        throw new Error(payload.message ?? `Disconnect failed (${response.status})`);
      }
      await loadInspectorStatus();
    } catch (error) {
      setInspectorError((error as Error).message);
    } finally {
      setDisconnectLoading(false);
    }
  };

  const verifyInspectorSite = async (instanceId: string, siteUrl: string): Promise<void> => {
    setGrantLoading((prev) => ({ ...prev, [instanceId]: true }));
    setGrantError((prev) => ({ ...prev, [instanceId]: '' }));
    try {
      const response = await fetch('/api/inspector/sites/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: selectedTenantId, siteUrl })
      });
      const payload = (await response.json().catch(() => ({}))) as { message?: string };
      if (!response.ok) {
        throw new Error(payload.message ?? `Verification failed (${response.status})`);
      }
      await loadInspectorStatus();
    } catch (error) {
      setGrantError((prev) => ({ ...prev, [instanceId]: (error as Error).message }));
    } finally {
      setGrantLoading((prev) => ({ ...prev, [instanceId]: false }));
    }
  };

  const revokeInspectorSite = async (instanceId: string, siteUrl: string): Promise<void> => {
    if (!window.confirm('Revoke Inspector use in Edinshall now? A customer administrator must separately remove the Microsoft 365 site assignment.')) return;
    setGrantLoading(prev => ({ ...prev, [instanceId]: true }));
    setGrantError(prev => ({ ...prev, [instanceId]: '' }));
    try {
      const response = await fetch('/api/inspector/sites/revoke', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: selectedTenantId, siteUrl })
      });
      const payload = (await response.json().catch(() => ({}))) as { message?: string };
      if (!response.ok) throw new Error(payload.message ?? `Revoke failed (${response.status})`);
      await loadInspectorStatus();
    } catch (error) {
      setGrantError(prev => ({ ...prev, [instanceId]: (error as Error).message }));
    } finally {
      setGrantLoading(prev => ({ ...prev, [instanceId]: false }));
    }
  };

  const inspectAdmins = async (instanceId: string): Promise<void> => {
    setInspectLoading((prev) => ({ ...prev, [instanceId]: true }));
    setInspectError((prev) => ({ ...prev, [instanceId]: '' }));
    try {
      const params = new URLSearchParams({ tenantId: selectedTenantId });
      const response = await fetch(`/api/instances/${encodeURIComponent(instanceId)}/admins?${params.toString()}`);
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(payload.message ?? `Inspection failed (${response.status})`);
      }
      const payload = (await response.json()) as {
        fetchedAt: string;
        cached: boolean;
        memberCount: number;
        members: InstanceAdminMember[];
      };
      setAdminInspections((prev) => ({
        ...prev,
        [instanceId]: {
          fetchedAt: payload.fetchedAt,
          cached: payload.cached,
          memberCount: payload.memberCount,
          members: payload.members
        }
      }));
    } catch (error) {
      setInspectError((prev) => ({
        ...prev,
        [instanceId]: (error as Error).message
      }));
    } finally {
      setInspectLoading((prev) => ({ ...prev, [instanceId]: false }));
    }
  };

  const updateInstanceListVisibility = (instanceId: string, listVisibility: NonNullable<InstanceView['listVisibility']>): void => {
    setInstances((prev) => prev.map((instance) => (
      instance.instanceId === instanceId
        ? { ...instance, listVisibility }
        : instance
    )));
  };

  const revealInstanceLists = async (instanceId: string): Promise<void> => {
    setListVisibilityLoading((prev) => ({ ...prev, [instanceId]: true }));
    setListVisibilityError((prev) => ({ ...prev, [instanceId]: '' }));
    try {
      const params = new URLSearchParams({ tenantId: selectedTenantId });
      const response = await fetch(`/api/instances/${encodeURIComponent(instanceId)}/list-visibility/reveal?${params.toString()}`, {
        method: 'POST'
      });
      const payload = (await response.json().catch(() => ({}))) as {
        message?: string;
        listVisibility?: NonNullable<InstanceView['listVisibility']>;
      };
      if (!response.ok || !payload.listVisibility) {
        throw new Error(payload.message ?? `Reveal failed (${response.status})`);
      }
      updateInstanceListVisibility(instanceId, payload.listVisibility);
    } catch (error) {
      setListVisibilityError((prev) => ({ ...prev, [instanceId]: (error as Error).message }));
    } finally {
      setListVisibilityLoading((prev) => ({ ...prev, [instanceId]: false }));
    }
  };

  const hideInstanceLists = async (instanceId: string): Promise<void> => {
    setListVisibilityLoading((prev) => ({ ...prev, [instanceId]: true }));
    setListVisibilityError((prev) => ({ ...prev, [instanceId]: '' }));
    try {
      const params = new URLSearchParams({ tenantId: selectedTenantId });
      const response = await fetch(`/api/instances/${encodeURIComponent(instanceId)}/list-visibility/hide?${params.toString()}`, {
        method: 'POST'
      });
      const payload = (await response.json().catch(() => ({}))) as {
        message?: string;
        listVisibility?: NonNullable<InstanceView['listVisibility']>;
      };
      if (!response.ok || !payload.listVisibility) {
        throw new Error(payload.message ?? `Hide failed (${response.status})`);
      }
      updateInstanceListVisibility(instanceId, payload.listVisibility);
    } catch (error) {
      setListVisibilityError((prev) => ({ ...prev, [instanceId]: (error as Error).message }));
    } finally {
      setListVisibilityLoading((prev) => ({ ...prev, [instanceId]: false }));
    }
  };

  const visibleInstances = useMemo(
    () => instances.filter((instance) => showDeregistered || (instance.status !== 'deregistered' && instance.status !== 'superseded')),
    [instances, showDeregistered]
  );

  const heartbeatSummary = useMemo(() => {
    const activeRows = instances.filter((instance) => instance.status !== 'deregistered' && instance.status !== 'superseded');
    const summaryCounts = { fresh: 0, low: 0, stale: 0 };
    activeRows.forEach((instance) => {
      const health = getHeartbeatHealth(instance.lastSeenAt);
      summaryCounts[health] += 1;
    });
    return summaryCounts;
  }, [instances]);

  const grantsBySite = useMemo(() => {
    const map = new Map<string, InspectorSiteGrant>();
    for (const grant of inspectorSiteGrants) {
      map.set(grant.siteUrlNormalized, grant);
    }
    return map;
  }, [inspectorSiteGrants]);

  const isConnected = inspectorConnection?.connectionStatus === 'connected';
  const interactiveInspectorActions = interactiveSetupAvailable && selectedTenantId === tenant?.tenantId;

  // Map the rich view objects we already have to the card model.
  const cardInstances = visibleInstances.map((instance) => ({
    instanceId: instance.instanceId,
    siteUrl: instance.siteUrl,
    pageUrl: instance.pageUrl,
    webPartVersion: instance.webPartVersion,
    schemaVersion: instance.schemaVersion,
    requiredSchemaVersion,
    mode: instance.mode,
    lastSeenAt: instance.lastSeenAt,
    daysSinceLastSeen: instance.daysSinceLastSeen,
    status: instance.status,
    isOutdated: instance.isOutdated,
    supersededBy: instance.supersededBy ?? null,
    listVisibility: instance.listVisibility
      ? {
          visibilityState: instance.listVisibility.visibilityState,
          lastAppliedAt: instance.listVisibility.lastAppliedAt,
          lastError: instance.listVisibility.lastError
        }
      : undefined,
    attention: instance.attention ?? null,
    maintenance: instance.maintenance ?? null,
    mailDelivery: instance.mailDelivery
  }));

  const inspectorStateLabel: string =
    !inspectorConfigured
      ? 'multi-tenant inspector not configured'
      : !inspectorConnection
      ? 'not connected'
      : inspectorConnection.connectionStatus;
  const inspectorStateClass: string =
    !inspectorConfigured
      ? 'pill warning'
      : !inspectorConnection
      ? 'pill warning'
      : inspectorConnection.connectionStatus === 'connected'
      ? 'pill success'
      : inspectorConnection.connectionStatus === 'error'
      ? 'pill danger'
      : 'pill warning';

  if (!canManageInstallations) {
    return (
      <div className="card">
        <h1 className="section-title">Installations</h1>
        <p className="muted">
          You do not have installation management access for this tenant. Ask a customer system admin
          to add you in Permissions, or use an Edinshall support/deployment role where appropriate.
        </p>
      </div>
    );
  }

  return (
    <div className="installations-page">
      <div className="installations-header">
        <h1 className="installations-header-title">Installations</h1>
        <span className="installations-header-spacer" />
        <AdminTenantSelector
          label="Tenant"
          value={selectedTenantId}
          options={deployableTenants}
          onChange={setSelectedTenantId}
          helperText="Tenant-scoped. Inspector actions need sign-in to the selected tenant."
        />
      </div>
      <div className="card" style={{ marginTop: '12px' }}>
        <p className="muted" style={{ margin: 0 }}>
          Deployment and installation actions use your existing Microsoft permissions in the selected tenant.
          Your organization must ensure the operator is already permissioned in that tenant/site (typically a
          customer system administrator with Global Admin or equivalent delegated app-catalog rights).
          Edinshall Audit does not create or elevate Microsoft permissions.
        </p>
      </div>

      <InstancesAttentionSummary instances={cardInstances} />

      <div className="installations-inspector">
        <span className="label">Inspector</span>
        <div className="pill-list">
          <span className={inspectorStateClass}>{inspectorStateLabel}</span>
          {inspectorLoading && <span className="badge-subtle">loading…</span>}
        </div>
        <div className="actions">
          <button className="btn secondary" type="button" onClick={() => void loadInspectorStatus()} disabled={inspectorLoading}>
            Refresh
          </button>
          <button
            className="btn secondary"
            type="button"
            onClick={() => void startInspectorConnect()}
            disabled={!inspectorConfigured || connectLoading || !interactiveInspectorActions}
          >
            {isConnected ? 'Reconnect tenant' : connectLoading ? 'Connecting…' : 'Connect tenant'}
          </button>
          <button
            className="btn secondary"
            type="button"
            onClick={() => void disconnectInspector()}
            disabled={!inspectorConfigured || !inspectorConnection || disconnectLoading || !interactiveInspectorActions}
          >
            {disconnectLoading ? 'Disconnecting…' : 'Disconnect'}
          </button>
        </div>
        {!interactiveInspectorActions && selectedTenantId && (
          <div className="note">
            Viewing {selectedTenant?.displayName || selectedTenantId} in read-only mode for delegated inspector actions. Sign in to that tenant to connect, grant, inspect, or change protected-list visibility.
          </div>
        )}
        {inspectorError && <div className="note" style={{ color: 'var(--danger)' }}>{inspectorError}</div>}
        {inspectorConnection?.lastError && <div className="note" style={{ color: 'var(--danger)' }}>{inspectorConnection.lastError}</div>}
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <span className="pill success">{summary?.totalActive ?? 0} active</span>
          {(summary?.needsUpdate ?? 0) > 0 && <span className="pill warning">{summary?.needsUpdate} needs update</span>}
          {(summary?.needsAttention ?? 0) > 0 && <span className="pill warning">{summary?.needsAttention} needs attention</span>}
          {(summary?.maintenanceRunning ?? 0) > 0 && <span className="pill success">{summary?.maintenanceRunning} maintenance running</span>}
          {(summary?.totalSuperseded ?? 0) > 0 && <span className="badge-subtle">{summary?.totalSuperseded} superseded</span>}
          {(summary?.totalDeregistered ?? 0) > 0 && <span className="badge-subtle">{summary?.totalDeregistered} deregistered</span>}
        </div>
        <span className="installations-header-spacer" />
        <div className="muted" style={{ fontSize: 12 }}>
          {currentVersion
            ? `Current release: v${currentVersion}${currentVersionSource === 'release-index' ? ' (release archive)' : currentVersionSource === 'config' ? ' (configured fallback)' : ''}`
            : 'Current release: not reported'}
        </div>
        <button className="btn secondary" type="button" onClick={() => void loadInstances()} disabled={loading}>
          Refresh
        </button>
        <button
          className="btn secondary"
          type="button"
          onClick={() => void verifyInstallations()}
          disabled={loading || verifyLoading || selectedTenantId !== tenant?.tenantId}
          title={
            selectedTenantId !== tenant?.tenantId
              ? 'Verification probes each site with your own SharePoint access, so it is only available for the tenant you are signed in to.'
              : 'Probe every registered site and deregister the installations of sites SharePoint no longer serves.'
          }
        >
          {verifyLoading ? 'Verifying…' : 'Verify installations'}
        </button>
        <button className="btn secondary" type="button" onClick={() => setShowDeregistered((v) => !v)}>
          {showDeregistered ? 'Hide superseded and deregistered' : 'Show superseded and deregistered'}
        </button>
      </div>

      {verifyError && (
        <div className="installation-notice notice-danger">
          <span className="icon" aria-hidden="true">✕</span>
          <div className="text">{verifyError}</div>
        </div>
      )}
      {verifyResult && !verifyError && (
        <div className={`installation-notice ${verifyResult.notFound > 0 || verifyResult.errors > 0 ? 'notice-warning' : 'notice-info'}`}>
          <span className="icon" aria-hidden="true">✓</span>
          <div className="text">
            <strong>Checked {verifyResult.checkedSites} site{verifyResult.checkedSites === 1 ? '' : 's'}.</strong>{' '}
            {verifyResult.reachable} reachable, {verifyResult.notFound} not found ({verifyResult.deregisteredCount} installation{verifyResult.deregisteredCount === 1 ? '' : 's'} deregistered), {verifyResult.noAccess} without access, {verifyResult.errors} with errors.
            {verifyResult.sites.filter((site) => site.outcome !== 'reachable').length > 0 && (
              <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                {verifyResult.sites
                  .filter((site) => site.outcome !== 'reachable')
                  .map((site) => (
                    <li key={site.siteUrl}>
                      {prettyHost(site.siteUrl)}: {site.outcome === 'not_found' ? 'not found, deregistered' : site.outcome === 'no_access' ? 'no access with your account, left as is' : 'could not be checked, left as is'}
                      {site.httpStatus ? ` (HTTP ${site.httpStatus})` : ''}
                    </li>
                  ))}
              </ul>
            )}
            {' '}
            <button className="btn secondary" type="button" style={{ marginLeft: 8 }} onClick={() => setVerifyResult(null)}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      {loading && <div className="badge-subtle">Loading installations…</div>}
      {error && !loading && <div className="installation-notice notice-danger"><span className="icon" aria-hidden="true">✕</span><div className="text">{error}</div></div>}
      {!loading && !error && degraded && (
        <div className="installation-notice notice-warning">
          <span className="icon" aria-hidden="true">⚠️</span>
          <div className="text">
            Installations are temporarily unavailable from inventory storage. Showing a degraded empty view.
          </div>
        </div>
      )}

      {!loading && !error && visibleInstances.length === 0 && (
        <div className="installation-card">
          <p className="muted">No installations detected yet.</p>
          <p className="muted">Deploy the Edinshall Audit web part to a SharePoint site to see it appear here.</p>
          <a className="btn secondary" href="/docs">View deployment guide</a>
        </div>
      )}

      {!loading && !error && visibleInstances.length > 0 && (
        <div className="installations-list">
          {visibleInstances.map((instance) => {
            const heartbeatHealth = getHeartbeatHealth(instance.lastSeenAt);
            const siteGrant = grantsBySite.get(normalizeSiteKey(instance.siteUrl));
            const siteGranted = siteGrant?.grantStatus === 'granted';
            const canInspectAdmins = interactiveInspectorActions && isConnected && siteGranted;
            const cardModel = cardInstances.find((entry) => entry.instanceId === instance.instanceId);
            if (!cardModel) return null;
            return (
              <InstanceCard
                key={instance.instanceId}
                instance={cardModel}
                siteGranted={siteGranted}
                canInspectAdmins={canInspectAdmins}
                isConnected={isConnected}
                interactiveInspectorActions={interactiveInspectorActions}
                inspectorAppClientId={inspectorAppClientId}
                loading={{
                  maintenance: maintenanceLoading[instance.instanceId] === true,
                  dismiss: dismissLoading[instance.instanceId] === true,
                  deregister: deregisterLoading[instance.instanceId] === true,
                  inspect: inspectLoading[instance.instanceId] === true,
                  listVisibility: listVisibilityLoading[instance.instanceId] === true,
                  grant: grantLoading[instance.instanceId] === true,
                  mailResend: mailResendLoading[instance.instanceId] === true
                }}
                errors={{
                  maintenance: maintenanceError[instance.instanceId],
                  dismiss: dismissError[instance.instanceId],
                  deregister: deregisterError[instance.instanceId],
                  inspect: inspectError[instance.instanceId],
                  listVisibility: listVisibilityError[instance.instanceId],
                  grant: grantError[instance.instanceId],
                  mailResend: mailResendError[instance.instanceId]
                }}
                adminInspection={adminInspections[instance.instanceId]}
                onRequestMaintenance={(action) => void requestMaintenance(instance.instanceId, action)}
                onDismissMaintenance={(operationId) => void dismissMaintenance(instance.instanceId, operationId)}
                onCancelMaintenance={(operationId) => void cancelMaintenance(instance.instanceId, operationId)}
                onDeregister={() => void deregisterInstance(instance.instanceId)}
                onInspectAdmins={() => void inspectAdmins(instance.instanceId)}
                onRevealLists={() => void revealInstanceLists(instance.instanceId)}
                onHideLists={() => void hideInstanceLists(instance.instanceId)}
                onGrantSite={() => void verifyInspectorSite(instance.instanceId, instance.siteUrl)}
                onRevokeSite={() => void revokeInspectorSite(instance.instanceId, instance.siteUrl)}
                onResendHeldMail={() => void resendHeldMail(instance.instanceId)}
                formatRelative={relativeTime}
                prettyHost={prettyHost}
                heartbeatHealth={heartbeatHealth}
                extraActionGroups={
                  <ZeroTouchCard tenantId={selectedTenantId} instanceId={instance.instanceId} />
                }
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Instances;
